import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Row, Col, Avatar, Tag, Typography, Space, Button, Input, Modal, Empty,
  Spin, Tooltip, Alert, message, Collapse,
} from 'antd';
import {
  RobotOutlined, MessageOutlined, LineOutlined, LinkOutlined,
  ReloadOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

// Build absolute URL for upstream public assets (avatars / QR / webchat).
// The base hostname is shipped by the backend in the /alayaess-agents response
// (`dispatch_public_url`), so /ai-config edits flow through automatically.
function makeAbsUrl(baseUrl) {
  return (maybeRelative) => {
    if (!maybeRelative) return null;
    if (/^https?:/i.test(maybeRelative)) return maybeRelative;
    return (baseUrl || 'https://alayaess.comboware.tw') + maybeRelative;
  };
}

export default function AlayaessBotsPage() {
  const [agents, setAgents] = useState([]);
  const [usage, setUsage] = useState({});
  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState(null);
  const [lineModal, setLineModal] = useState(null);
  const [publicBase, setPublicBase] = useState('https://alayaess.comboware.tw');

  const absUrl = useMemo(() => makeAbsUrl(publicBase), [publicBase]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [agentsRes, usageRes, bindingsRes] = await Promise.all([
        aiSalesAPI.listAlayaessAgents(),
        aiSalesAPI.alayaessAgentUsage(),
        aiSalesAPI.listAlayaessBindings(),
      ]);
      const ab = agentsRes.data ?? agentsRes;
      const ub = usageRes.data ?? usageRes;
      const bb = bindingsRes.data ?? bindingsRes;
      setConfigured(ab.configured);
      if (!ab.ok) setLoadError(ab.error);
      setAgents(ab.agents || []);
      setUsage(ub.usage || {});
      setBindings(bb.rows || []);
      if (ab.dispatch_public_url) setPublicBase(ab.dispatch_public_url);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const tenants = useMemo(() => {
    const s = new Set(agents.map(a => a.tenant_id).filter(Boolean));
    return Array.from(s).sort();
  }, [agents]);

  const unbound = useMemo(
    () => bindings.filter(r => !r.alayaess_bot_name),
    [bindings],
  );
  const KIND_META = {
    ai_sales_bot:   { label: 'AI Sales Bot', color: 'blue' },
    chatbot_config: { label: 'Chatbot',      color: 'purple' },
    scenario_site:  { label: 'Scenario Site', color: 'green' },
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter(a => {
      if (tenantFilter && a.tenant_id !== tenantFilter) return false;
      if (!q) return true;
      return [a.bot_name, a.display_name, a.tenant_id, a.title, a.persona]
        .filter(Boolean)
        .some(s => s.toLowerCase().includes(q));
    });
  }, [agents, search, tenantFilter]);

  const handleOpenWebChat = (agent) => {
    const url = absUrl(agent.webchat_url);
    if (!url) {
      message.warning('該 agent 沒有 webchat_url');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (configured === false) {
    return (
      <div>
        <Title level={4} style={{ margin: 0 }}><RobotOutlined /> AlayaEss Bot 目錄</Title>
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="尚未設定 DISPATCH_DIRECTORY_TOKEN"
          description={
            <>
              請到 <Link to="/ai-config">AI 模型設定</Link> →「AlayaEss Dispatch」群組，把
              directory scope 的 token 貼到 <code>DISPATCH_DIRECTORY_TOKEN</code> 欄位再儲存。
              貼好後重新整理本頁即可看到 tenant 內的所有 bot。
            </>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <RobotOutlined /> AlayaEss Bot 目錄
          </Title>
          <Text type="secondary">
            從 AlayaEss 拉到的 {agents.length} 個 bot。點卡片上的 WebChat 直接跟 bot 對話，或掃 LINE QR 加 LINE 好友。「SPO 綁定 →」開啟綁定管理頁。
          </Text>
        </div>
        <Space>
          <Input.Search
            placeholder="搜尋 bot / tenant / persona"
            allowClear
            style={{ width: 240 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新整理</Button>
        </Space>
      </div>

      {loadError && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }}
               message="載入 agents 失敗" description={loadError} />
      )}

      {unbound.length > 0 && (
        <Collapse
          style={{ marginBottom: 16 }}
          items={[{
            key: 'unbound',
            label: (
              <span>
                ⚠️ 還有 <b>{unbound.length}</b> 個 SPO bot 未綁定 AlayaEss agent
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  （點展開查看，可一鍵跳去綁定頁）
                </Text>
              </span>
            ),
            children: (
              <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                {unbound.map(r => {
                  const meta = KIND_META[r.kind] || { label: r.kind, color: 'default' };
                  return (
                    <Card
                      key={`${r.kind}-${r.id}`}
                      size="small"
                      style={{ width: 260 }}
                      bodyStyle={{ padding: 10 }}
                    >
                      <Tag color={meta.color} style={{ marginBottom: 4 }}>{meta.label}</Tag>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                        {r.subtitle}
                      </Text>
                      <Link to={`/alayaess-bindings?highlight=${r.kind}:${r.id}`}>
                        <Button type="primary" size="small" block icon={<LinkOutlined />}>
                          去綁定 →
                        </Button>
                      </Link>
                    </Card>
                  );
                })}
              </Space>
            ),
          }]}
        />
      )}

      {tenants.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ marginRight: 8 }}>Tenant 篩選：</Text>
          <Space wrap>
            <Tag
              color={tenantFilter === null ? 'blue' : 'default'}
              style={{ cursor: 'pointer' }}
              onClick={() => setTenantFilter(null)}
            >全部 ({agents.length})</Tag>
            {tenants.map(t => {
              const n = agents.filter(a => a.tenant_id === t).length;
              return (
                <Tag
                  key={t}
                  color={tenantFilter === t ? 'blue' : 'default'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setTenantFilter(t === tenantFilter ? null : t)}
                >{t} ({n})</Tag>
              );
            })}
          </Space>
        </div>
      )}

      <Spin spinning={loading}>
        {filtered.length === 0 ? (
          <Empty description="沒有符合條件的 bot" />
        ) : (
          <Row gutter={[16, 16]}>
            {filtered.map(a => {
              const bindings = usage[a.bot_name] || [];
              const bindingTags = bindings.map((b, i) => (
                <Tag key={`${b.kind}-${b.id}-${i}`} color={
                  b.kind === 'ai_sales_bot' ? 'blue'
                  : b.kind === 'chatbot_config' ? 'purple'
                  : 'green'
                } style={{ marginTop: 4 }}>
                  {b.name}
                </Tag>
              ));
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={a.bot_name}>
                  <Card
                    hoverable
                    bodyStyle={{ padding: 16 }}
                    style={{ height: '100%' }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                      <Avatar
                        size={56}
                        src={absUrl(a.avatar_url)}
                        icon={<RobotOutlined />}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{a.display_name || a.bot_name}</div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          <code style={{ fontSize: 11 }}>{a.bot_name}</code>
                        </Text>
                        <Space size={4} wrap style={{ marginTop: 2 }}>
                          {a.tenant_id && (
                            <Tag color="default" style={{ margin: 0 }}>
                              <GlobalOutlined /> {a.tenant_id}
                            </Tag>
                          )}
                          {a.title && (
                            <Tag color="cyan" style={{ margin: 0 }}>{a.title}</Tag>
                          )}
                        </Space>
                      </div>
                    </div>

                    {a.persona && (
                      <Paragraph
                        ellipsis={{ rows: 2 }}
                        style={{ marginBottom: 8, color: '#475569', fontSize: 13 }}
                      >
                        💬 {a.persona}
                      </Paragraph>
                    )}

                    <div style={{ marginBottom: 12, fontSize: 12, color: '#64748b' }}>
                      <Space wrap size={4}>
                        {a.has_role_override
                          ? <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>✓ role_override</Tag>
                          : <Tag style={{ margin: 0, fontSize: 11 }}>default role</Tag>}
                        {a.line_count > 0 && (
                          <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                            <LineOutlined /> LINE × {a.line_count}
                          </Tag>
                        )}
                      </Space>
                    </div>

                    {bindings.length > 0 ? (
                      <div style={{ marginBottom: 12, padding: '6px 8px', background: '#f0f9ff', borderRadius: 4 }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                          🔗 已綁到 {bindings.length} 個 SPO entity:
                        </Text>
                        <div>{bindingTags}</div>
                      </div>
                    ) : (
                      <div style={{ marginBottom: 12 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>尚未被任何 SPO entity 綁定</Text>
                      </div>
                    )}

                    <Space direction="vertical" style={{ width: '100%' }} size={4}>
                      <Space style={{ width: '100%' }}>
                        <Tooltip title={`開啟 ${absUrl(a.webchat_url)}`}>
                          <Button
                            type="primary"
                            icon={<MessageOutlined />}
                            onClick={() => handleOpenWebChat(a)}
                            block
                          >
                            WebChat
                          </Button>
                        </Tooltip>
                        <Button
                          icon={<LineOutlined />}
                          disabled={a.line_count === 0}
                          onClick={() => setLineModal(a)}
                          block
                        >
                          LINE QR
                        </Button>
                      </Space>
                      <Link to="/alayaess-bindings">
                        <Button block size="small" icon={<LinkOutlined />}>SPO 綁定 →</Button>
                      </Link>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>

      {/* LINE channels modal */}
      <Modal
        open={!!lineModal}
        onCancel={() => setLineModal(null)}
        title={lineModal ? `${lineModal.display_name || lineModal.bot_name} 的 LINE Channels` : ''}
        footer={null}
        width={520}
      >
        {lineModal && (lineModal.line_channels || []).map((ch) => (
          <Card key={ch.channel_key} size="small" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <img
                src={absUrl(ch.qr_png_url)}
                alt="LINE QR"
                style={{ width: 140, height: 140, border: '1px solid #e5e7eb', borderRadius: 8 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{ch.display_name || lineModal.display_name}</div>
                {ch.basic_id && (
                  <Text copyable={{ text: ch.basic_id }} style={{ fontSize: 13, color: '#475569' }}>
                    Basic ID: {ch.basic_id}
                  </Text>
                )}
                <div style={{ marginTop: 8 }}>
                  <a href={absUrl(ch.landing_url)} target="_blank" rel="noopener noreferrer">
                    <Button type="primary" size="small" icon={<LinkOutlined />}>加入好友落地頁</Button>
                  </a>
                </div>
                <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                  channel_key: <code style={{ fontSize: 10 }}>{ch.channel_key}</code>
                </Text>
              </div>
            </div>
          </Card>
        ))}
      </Modal>
    </div>
  );
}
